/**
 * Technology lifecycle register adapter (2026-10-20).
 *
 * One row per product: manufacturer, model, the end of mainstream and
 * of extended support, and the maker's notice the dates were taken
 * from. Configuration items are matched to a row by manufacturer and
 * model, which is how technology currency judges them; each row shows
 * how many items it currently matches, because a row matching none is
 * usually a spelling the CMDB does not use.
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export interface TechnologyLifecycleEntry {
  id: string;
  manufacturer: string;
  model: string;
  mainstream_support_end: string | null;
  extended_support_end: string | null;
  source: string;
  review_due: string | null;
  notes: string | null;
  replacement_cost_sar: string | null;
  matched_configuration_items: number;
}

export interface TechnologyLifecycleIn {
  manufacturer: string;
  model: string;
  mainstream_support_end: string | null;
  extended_support_end: string | null;
  source: string;
  review_due: string | null;
  notes: string | null;
  replacement_cost_sar: string | number | null;
}

export type TechnologyLifecyclePatch = Partial<TechnologyLifecycleIn>;

const dash = <span className="text-xs text-slate-400">--</span>;

const columns: AdminColumn<TechnologyLifecycleEntry>[] = [
  {
    key: "product",
    label: "Product",
    sortValue: (r) => `${r.manufacturer} ${r.model}`,
    render: (r) => (
      <span className="font-medium text-slate-900">
        {r.manufacturer} {r.model}
      </span>
    ),
  },
  {
    key: "mainstream",
    label: "Mainstream support ends",
    sortValue: (r) => r.mainstream_support_end,
    render: (r) =>
      r.mainstream_support_end ? (
        <span className="text-xs text-slate-700">{r.mainstream_support_end}</span>
      ) : (
        dash
      ),
  },
  {
    key: "extended",
    label: "Extended support ends",
    sortValue: (r) => r.extended_support_end,
    render: (r) =>
      r.extended_support_end ? (
        <span className="text-xs text-slate-700">{r.extended_support_end}</span>
      ) : (
        dash
      ),
  },
  {
    key: "matched",
    label: "Items matched",
    align: "right",
    sortValue: (r) => r.matched_configuration_items,
    render: (r) => (
      <span
        data-testid="tle-matched"
        className={
          r.matched_configuration_items === 0
            ? "text-xs text-amber-800"
            : "text-xs text-slate-700"
        }
      >
        {r.matched_configuration_items}
      </span>
    ),
  },
  {
    key: "source",
    label: "Source",
    render: (r) => (
      <span className="text-xs text-slate-600 line-clamp-2">{r.source}</span>
    ),
  },
];

const formFields: FormField<TechnologyLifecycleIn>[] = [
  {
    kind: "text",
    key: "manufacturer",
    label: "Manufacturer",
    required: true,
    maxLength: 255,
  },
  { kind: "text", key: "model", label: "Model", required: true, maxLength: 255 },
  { kind: "date", key: "mainstream_support_end", label: "Mainstream support ends" },
  { kind: "date", key: "extended_support_end", label: "Extended support ends" },
  {
    kind: "textarea",
    key: "source",
    label: "Source (the maker's notice the dates come from)",
    rows: 2,
  },
  { kind: "date", key: "review_due", label: "Review due" },
  {
    kind: "decimal",
    key: "replacement_cost_sar",
    label: "Replacement cost per item",
    currencyHint: "SAR",
  },
  { kind: "textarea", key: "notes", label: "Notes", rows: 2 },
];

export const technologyLifecycleAdapter: EntityAdapter<
  TechnologyLifecycleEntry,
  TechnologyLifecycleIn,
  TechnologyLifecyclePatch
> = {
  i18nPrefix: "modules.foundation.admin.technologyLifecycle",
  endpoint: "/v1/admin/foundation/technology-lifecycle",
  queryKey: ["foundation-admin", "technology-lifecycle"] as const,
  columns,
  defaultSort: { key: "product", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.manufacturer} ${r.model} ${r.source}`,
  formFields,
  formInitial: () => ({
    manufacturer: "",
    model: "",
    mainstream_support_end: null,
    extended_support_end: null,
    source: "",
    review_due: null,
    notes: null,
    replacement_cost_sar: null,
  }),
  rowToPatch: (r) => ({
    manufacturer: r.manufacturer,
    model: r.model,
    mainstream_support_end: r.mainstream_support_end,
    extended_support_end: r.extended_support_end,
    source: r.source,
    review_due: r.review_due,
    notes: r.notes,
    replacement_cost_sar: r.replacement_cost_sar,
  }),
};
